/**
 * Picker → ADF storage adapter.
 *
 * If the hex emitted by the color-picker matches a shade in the active brand
 * palette, persist the CSS-var reference for that shade (e.g.
 * `"var(--hub-color-primary-300)"`) so the value tracks future brand-color
 * edits via the browser's CSS cascade — no resolver call, no JS palette
 * lookup at render time. Otherwise persist the raw hex (frozen literal).
 *
 * Pure case-insensitive lookup, no normalisation — relies on the picker
 * emitting hex in the same casing the palette uses (`csm-colour-theme-generator`
 * outputs lowercase). Pure, synchronous, no side effects → SSR-safe.
 */
import { HUB_COLOR_CSS_VARS } from './hub-css-vars';
import type { HubBrandPalette, PaletteRef } from './types';

export const toAdfColor = (hex: string, palette: HubBrandPalette): string => {
	const lower = hex.toLowerCase();
	for (const [ref, shadeHex] of Object.entries(palette) as Array<[PaletteRef, string]>) {
		if (shadeHex.toLowerCase() === lower) {
			// Hex fallback after the CSS var name lets the value still resolve
			// to a usable color when the wrapper isn't in scope (e.g. shared
			// extensions rendered in Confluence Company Hub, or color helpers
			// like `extractColorCode` that look up the var on `documentElement`
			// rather than the wrapper). The browser uses the wrapper's live
			// value when the var is defined (so brand-color edits still track
			// inside the hub), and falls back to this snapshot hex otherwise.
			return `var(${HUB_COLOR_CSS_VARS[ref]}, ${shadeHex})`;
		}
	}
	return hex;
};
